/* eslint-disable max-len */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-curly-newline */
import { useCallback, useMemo, useRef } from 'react';

import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PROJECT_ROLES } from 'constant';

import PropTypes from 'prop-types';
import { getAcadamicLevelShortHand, getContactOrganizationName, is1593 } from 'utils/func';
import SortableList from 'components/SortableList';

import ResearcherDialog from './ResearcherDialog';
import DownloadScientificProfile from '../../DownloadScientificProfile';
import Warning from '../../Warning';
import ResearchHeading from './ResearchHeading';
import ResearchRow from './ResearchRow';

export default function ResearchersTable({
  organizations,
  projectRoles,
  disabled,
  watch,
  control,
  onBindingIntoContactFieldClick,
  handleDownloadScientificProfileFile,
}) {
  // #region Data
  const { t } = useTranslation();
  const template = watch('general.template');
  const owner = watch('general.owner');
  const scientificSecretary = watch('general.scientificSecretary');

  const researcherRef = useRef();

  const {
    fields: scientificSecretaryFields,
    append: appendScientificSecretary,
    remove: removeScientificSecretary,
    update: updateScientificSecretary,
    replace: replaceScientificSecretaries,
  } = useFieldArray({
    control,
    name: 'general.scientificSecretaries',
  });

  const {
    fields: mainParticipantFields,
    append: appendMainParticipant,
    remove: removeMainParticipant,
    update: updateMainParticipant,
    replace: replaceMainParticipants,
  } = useFieldArray({
    control,
    name: 'general.mainParticipants',
  });

  const {
    fields: participantFields,
    append: appendParticipant,
    remove: removeParticipant,
    update: updateParticipant,
    replace: replaceParticipants,
  } = useFieldArray({
    control,
    name: 'general.participants',
  });

  const {
    fields: technicianFields,
    append: appendTechnician,
    remove: removeTechnician,
    update: updateTechnician,
    replace: replaceTechnicians,
  } = useFieldArray({
    control,
    name: 'general.technicians',
  });

  const {
    fields: domesticExpertFields,
    append: appendDomesticExpert,
    remove: removeDomesticExpert,
    update: updateDomesticExpert,
    replace: replaceDomesticExperts,
  } = useFieldArray({
    control,
    name: 'general.domesticExperts',
  });

  const {
    fields: internationalExpertFields,
    append: appendInternationalExpert,
    remove: removeInternationalExpert,
    update: updateInternationalExpert,
    replace: replaceInternationalExperts,
  } = useFieldArray({
    control,
    name: 'general.internationalExperts',
  });
  // #endregion Data

  // #region Event
  const getProjectRoleId = (roleCode) => {
    const projectRole = projectRoles.find((role) => role.code === roleCode);
    if (projectRole) return projectRole.id;
    return '';
  };

  const handleOpenAppendMemberModal = useCallback(
    (projectRoleId, append, order) => {
      researcherRef.current.open({
        projectRoleId,
        append,
        type: 'APPEND',
        value: { order },
      });
    },
    [researcherRef.current]
  );

  const renderItemTemplate = useCallback(
    (item, index, config) => (
      <ResearchRow
        item={item}
        index={index}
        config={config}
        disabled={disabled}
        handleDownloadFile={handleDownloadScientificProfileFile}
        onBindingIntoContactFieldClick={onBindingIntoContactFieldClick}
        researcherRef={researcherRef}
        organizations={organizations}
      />
    ),
    [
      handleDownloadScientificProfileFile,
      onBindingIntoContactFieldClick,
      researcherRef,
      organizations,
      disabled,
    ]
  );

  const secretaryConfig = useMemo(
    () => ({
      scientificProfileToolTipTarget: 'researcher-scientific-secretary',
      updateItem: updateScientificSecretary,
      removeItem: removeScientificSecretary,
      projectRoleId: getProjectRoleId(PROJECT_ROLES.SECRETARY),
    }),
    [updateScientificSecretary, removeScientificSecretary, projectRoles]
  );
  const handleSecretaryItemTemplate = useCallback(
    (item, index) => renderItemTemplate(item, index, secretaryConfig),
    [renderItemTemplate, secretaryConfig]
  );

  const mainParticipantConfig = useMemo(
    () => ({
      scientificProfileToolTipTarget: 'researcher-main-participant',
      updateItem: updateMainParticipant,
      removeItem: removeMainParticipant,
      projectRoleId: getProjectRoleId(PROJECT_ROLES.MAIN_PARTICIPANT),
    }),
    [updateMainParticipant, removeMainParticipant, projectRoles]
  );

  const handleMainParticipantItemTemplate = useCallback(
    (item, index) => renderItemTemplate(item, index, mainParticipantConfig),
    [renderItemTemplate, mainParticipantConfig]
  );

  const participantConfig = useMemo(
    () => ({
      scientificProfileToolTipTarget: 'researcher-participant',
      updateItem: updateParticipant,
      removeItem: removeParticipant,
      projectRoleId: getProjectRoleId(PROJECT_ROLES.PARTICIPANT),
    }),
    [updateParticipant, removeParticipant, projectRoles]
  );
  const handleParticipantItemTemplate = useCallback(
    (item, index) => renderItemTemplate(item, index, participantConfig),
    [renderItemTemplate, participantConfig]
  );

  const technicianConfig = useMemo(
    () => ({
      scientificProfileToolTipTarget: 'researcher-technician',
      updateItem: updateTechnician,
      removeItem: removeTechnician,
      projectRoleId: getProjectRoleId(PROJECT_ROLES.TECHNICIAN),
    }),
    [updateTechnician, removeTechnician, projectRoles]
  );

  const handleTechnicianItemTemplate = useCallback(
    (item, index) => renderItemTemplate(item, index, technicianConfig),
    [renderItemTemplate, technicianConfig]
  );

  const domesticExportConfig = useMemo(
    () => ({
      scientificProfileToolTipTarget: 'researcher-domestic-expert',
      updateItem: updateDomesticExpert,
      removeItem: removeDomesticExpert,
      projectRoleId: getProjectRoleId(PROJECT_ROLES.DOMESTIC_EXPERT),
    }),
    [updateDomesticExpert, removeDomesticExpert, projectRoles]
  );
  const handleDomesticExpertItemTemplate = useCallback(
    (item, index) => renderItemTemplate(item, index, domesticExportConfig),
    [renderItemTemplate, domesticExportConfig]
  );

  const internationalExpertConfig = useMemo(
    () => ({
      scientificProfileToolTipTarget: 'researcher-international-expert',
      updateItem: updateInternationalExpert,
      removeItem: removeInternationalExpert,
      projectRoleId: getProjectRoleId(PROJECT_ROLES.INTERNATIONAL_EXPERT),
    }),
    [updateInternationalExpert, removeInternationalExpert, projectRoles]
  );

  const handleInternationalExpertItemTemplate = useCallback(
    (item, index) => renderItemTemplate(item, index, internationalExpertConfig),
    [renderItemTemplate, internationalExpertConfig]
  );

  // #endregion Event

  return (
    <div className="form-table">
      <table className="mb-2">
        <thead>
          <tr>
            <th className="text-center" style={{ minWidth: 50, width: 50 }}>
              {t('formLayout.numericalOrder')}
            </th>
            <th className="text-center" style={{ minWidth: 200, width: '40%' }}>
              {`${t('projectProposal.general.researchHumanResources.fields.academicRank')}, ${t(
                'projectProposal.general.researchHumanResources.fields.academicDegree'
              )}, ${t('projectProposal.general.researchHumanResources.fields.name')}`}
            </th>
            <th className="text-center" style={{ minWidth: 100, width: '5%' }}>
              {t('projectProposal.general.researchHumanResources.fields.gender')}
            </th>
            <th className="text-center" style={{ minWidth: 200, width: '40%' }}>
              {t('projectProposal.general.researchHumanResources.fields.organization')}
            </th>
            {!disabled && (
              <th className="float text-center" style={{ minWidth: 80, width: 80 }}>
                {t('projectProposal.action')}
              </th>
            )}
          </tr>
        </thead>
        {
          <tbody>
            {/* Owner */}
            <ResearchHeading
              label="projectProposal.general.researchHumanResources.ownerFields.label"
              disabled
            />
            <tr>
              <td className="text-center"> </td>
              <td>
                {`${getAcadamicLevelShortHand(
                  owner?.academicRank,
                  owner?.academicDegree,
                  t,
                  true
                )}${owner?.fullname}`}
                {owner?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={owner?.id || owner?.contact?.id || owner?.contactId}
                    contactName={owner?.fullname}
                    academicRank={owner?.academicRank}
                    academicDegree={owner?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget="researcher-owner"
                  />
                )}
              </td>
              <td className="text-center">{t(`userProfile.gender.${owner?.gender}`)}</td>
              <td>{getContactOrganizationName(organizations, owner?.contact, t)}</td>
              <td className="float" />
            </tr>
          </tbody>
        }

        {/* Scientific Secretary */}
        {is1593(template) && scientificSecretary?.fullname && (
          <tbody>
            <ResearchHeading
              label="projectProposal.general.researchHumanResources.scientificSecretaryFields.label"
              disabled
            />
            <tr>
              <td className="text-center"> </td>
              <td>
                {`${getAcadamicLevelShortHand(
                  scientificSecretary?.academicRank,
                  scientificSecretary?.academicDegree,
                  t,
                  true
                )}${scientificSecretary?.fullname}`}
                {scientificSecretary?.contact?.user?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={
                      scientificSecretary?.contact?.id ||
                      scientificSecretary?.id ||
                      scientificSecretary?.contactId
                    }
                    contactName={scientificSecretary?.fullname}
                    academicRank={scientificSecretary?.academicRank}
                    academicDegree={scientificSecretary?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget="researcher-scientific-secretary"
                  />
                )}
              </td>
              <td className="text-center">
                {t(`userProfile.gender.${scientificSecretary?.gender}`)}
              </td>
              <td>{getContactOrganizationName(organizations, scientificSecretary?.contact, t)}</td>
            </tr>
          </tbody>
        )}

        {/* Scientific Secretaries */}

        {!is1593(template) && (
          <>
            <ResearchHeading
              label="projectProposal.general.researchHumanResources.scientificSecretaryFields.label"
              className="add-scientific-secretary"
              tooltipTarget=".add-scientific-secretary"
              disabled={disabled || is1593(template)}
              projectRoleId={getProjectRoleId(PROJECT_ROLES.SECRETARY)}
              append={appendScientificSecretary}
              onOpenAppendModal={handleOpenAppendMemberModal}
              length={scientificSecretaryFields.length}
            />
            <SortableList
              items={scientificSecretaryFields}
              replaceItems={replaceScientificSecretaries}
              itemTemplate={handleSecretaryItemTemplate}
              disabled={disabled}
            />
          </>
        )}

        {/* Main participants */}
        <tbody>
          <ResearchHeading
            label="projectProposal.general.researchHumanResources.mainParticipantFields.label"
            className="add-main-participant"
            tooltipTarget=".add-main-participant"
            disabled={disabled}
            projectRoleId={getProjectRoleId(PROJECT_ROLES.MAIN_PARTICIPANT)}
            append={appendMainParticipant}
            onOpenAppendModal={handleOpenAppendMemberModal}
            length={mainParticipantFields.length}
          />
          <SortableList
            items={mainParticipantFields}
            replaceItems={replaceMainParticipants}
            itemTemplate={handleMainParticipantItemTemplate}
            disabled={disabled}
          />
        </tbody>

        {/* Participants */}
        <tbody>
          <ResearchHeading
            label="projectProposal.general.researchHumanResources.participantFields.label"
            className="add-participant"
            tooltipTarget=".add-participant"
            disabled={disabled}
            projectRoleId={getProjectRoleId(PROJECT_ROLES.PARTICIPANT)}
            append={appendParticipant}
            onOpenAppendModal={handleOpenAppendMemberModal}
            length={participantFields.length}
          />
          <SortableList
            items={participantFields}
            replaceItems={replaceParticipants}
            itemTemplate={handleParticipantItemTemplate}
            disabled={disabled}
          />
        </tbody>

        {/* Technicians */}
        <ResearchHeading
          label="projectProposal.general.researchHumanResources.technicianFields.label"
          className="add-technician"
          tooltipTarget=".add-technician"
          disabled={disabled}
          projectRoleId={getProjectRoleId(PROJECT_ROLES.TECHNICIAN)}
          append={appendTechnician}
          onOpenAppendModal={handleOpenAppendMemberModal}
          length={technicianFields.length}
        />
        <SortableList
          items={technicianFields}
          replaceItems={replaceTechnicians}
          itemTemplate={handleTechnicianItemTemplate}
          disabled={disabled}
        />

        {/* Domestic experts */}
        <ResearchHeading
          label="projectProposal.general.researchHumanResources.domesticExpertFields.label"
          className="add-domestic-expert"
          tooltipTarget=".add-domestic-expert"
          disabled={disabled}
          projectRoleId={getProjectRoleId(PROJECT_ROLES.DOMESTIC_EXPERT)}
          append={appendDomesticExpert}
          onOpenAppendModal={handleOpenAppendMemberModal}
          length={domesticExpertFields.length}
        />
        <SortableList
          items={domesticExpertFields}
          replaceItems={replaceDomesticExperts}
          itemTemplate={handleDomesticExpertItemTemplate}
          disabled={disabled}
        />

        {/* International experts */}
        <ResearchHeading
          label="projectProposal.general.researchHumanResources.internationalExpertFields.label"
          className="add-international-expert"
          tooltipTarget=".add-international-expert"
          disabled={disabled}
          projectRoleId={getProjectRoleId(PROJECT_ROLES.INTERNATIONAL_EXPERT)}
          append={appendInternationalExpert}
          onOpenAppendModal={handleOpenAppendMemberModal}
          length={internationalExpertFields.length}
        />
        <SortableList
          items={internationalExpertFields}
          replaceItems={replaceInternationalExperts}
          itemTemplate={handleInternationalExpertItemTemplate}
          disabled={disabled}
        />
      </table>

      <ResearcherDialog
        ref={researcherRef}
        organizations={organizations}
        ownerId={owner?.contact?.userId ?? ''}
      />
    </div>
  );
}

ResearchersTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  projectRoles: PropTypes.array,
  disabled: PropTypes.bool,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  onBindingIntoContactFieldClick: PropTypes.func.isRequired,
  handleDownloadScientificProfileFile: PropTypes.func.isRequired,
};

ResearchersTable.defaultProps = {
  organizations: [],
  projectRoles: [],
  disabled: false,
};
